import React, { useEffect, useMemo, useState } from 'react'
import { ExternalLink, Building2, Star } from 'lucide-react'
import { MarketViewSearchResult, MarketViewVendor, MarketViewScores } from '../../services/api'
import ScoreRadarChart from './ScoreRadarChart'
import {
  describeOverall,
  describeScore,
  overallBadgeClasses,
} from '../../lib/scoreDisplay'
import Badge from '../ui/Badge'
import { getUniqueAgencyBadges } from '../../lib/agencyBadges'

/** Compact agency relationship badges shown below the vendor name. */
const AgencyBadges: React.FC<{ vendor: MarketViewVendor }> = ({ vendor }) => {
  const agencyBadges = useMemo(
    () => getUniqueAgencyBadges(vendor.agency_relationships || []),
    [vendor.agency_relationships]
  )

  if (agencyBadges.length === 0) {
    return null
  }

  return (
    <div className="flex items-center gap-1 mt-1 flex-wrap">
      {/* Agency relationship badges */}
      {agencyBadges.slice(0, 3).map((badge) => (
        <span
          key={badge.label}
          className={`inline-flex items-center px-1.5 py-0 rounded text-[10px] font-semibold ${badge.className}`}
        >
          {badge.label}
        </span>
      ))}
      {agencyBadges.length > 3 && (
        <span className="text-[10px] text-muted-foreground">
          +{agencyBadges.length - 3}
        </span>
      )}
    </div>
  )
}

/** Compact 2-bar display for standalone mode (need_fit + technical only). */
const TwoScoreBars: React.FC<{ scores: MarketViewScores }> = ({ scores }) => {
  const bars = [
    { label: 'Need Fit', score: describeScore(scores.need_fit) },
    { label: 'Technical', score: describeScore(scores.technical) },
  ]

  const barColor = (v: number) => {
    if (v >= 80) return 'bg-success'
    if (v >= 60) return 'bg-primary'
    if (v >= 40) return 'bg-warning'
    return 'bg-destructive'
  }

  return (
    <div className="space-y-2 py-2">
      {bars.map(({ label, score }) => (
        <div key={label} title={score.explanation || undefined}>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[10px] text-muted-foreground font-medium">{label}</span>
            <span
              className={`text-[10px] ${
                score.isScored
                  ? 'font-semibold text-foreground'
                  : 'text-muted-foreground'
              }`}
            >
              {score.text}
            </span>
          </div>
          {score.isScored ? (
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                data-testid="score-bar-fill"
                className={`h-full rounded-full transition-all ${barColor(score.value)}`}
                style={{ width: `${score.value}%` }}
              />
            </div>
          ) : (
            // A flat dashed rule, not an empty track: an empty track reads as a
            // zero score or as a bar still loading (DEV-584).
            <div
              data-testid="score-bar-empty"
              className="h-1.5 rounded-full border-t border-dashed border-border"
            />
          )}
        </div>
      ))}
    </div>
  )
}

interface ProductResultCardProps {
  result: MarketViewSearchResult
  onClick?: () => void
  programId?: string
  solutionFamilyId?: string | null
  /** Optimistic: fired immediately on click, and again with the old value if
   *  the save fails. Good for list state, wrong for anything that re-reads the
   *  server — use `onShortlistPersisted` for that. */
  onShortlistToggle?: (productId: number, vendorId: number, isShortlisted: boolean) => void
  /** Fired once the shortlist request has settled (saved or reverted), so a
   *  parent that re-reads server state doesn't race the round-trip. */
  onShortlistPersisted?: () => void
}

const ProductResultCard: React.FC<ProductResultCardProps> = ({
  result,
  onClick,
  programId,
  solutionFamilyId,
  onShortlistToggle,
  onShortlistPersisted,
}) => {
  const { product, vendor, scores, semantic_score, is_shortlisted } = result
  const [isShortlisted, setIsShortlisted] = useState(is_shortlisted ?? false)
  const [isTogglingShortlist, setIsTogglingShortlist] = useState(false)

  // Cards are reused across pagination/search; the initial useState seed only
  // applies on mount, so sync local state when the underlying result changes.
  useEffect(() => {
    setIsShortlisted(is_shortlisted ?? false)
  }, [is_shortlisted, product.id, vendor.id])

  // Detect standalone mode: the vendor scorecards are not applicable without a
  // program. 'not_applicable' is specifically the standalone case - a program
  // whose scorecard simply has not been run yet is 'not_scored' and keeps the
  // radar.
  const isStandaloneMode =
    describeScore(scores.manufacturing).state === 'not_applicable'

  // Use primary image if available, fall back to legacy image_url
  const rawImageUrl = product.primary_image?.image_url || product.image_url

  // In dev, proxy external images to bypass CORS
  const isDev = import.meta.env.DEV
  const API_BASE_URL = import.meta.env.VITE_API_URL || ''
  const productImageUrl = rawImageUrl && isDev && rawImageUrl.startsWith('http')
    ? `${API_BASE_URL}/api/proxy-image/?url=${encodeURIComponent(rawImageUrl)}`
    : rawImageUrl

  const imageCount = product.images?.length || 0
  const overall = describeOverall(scores)

  const handleShortlistClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!programId || isTogglingShortlist) return

    setIsTogglingShortlist(true)
    const newValue = !isShortlisted
    setIsShortlisted(newValue)

    if (onShortlistToggle) {
      onShortlistToggle(product.id, vendor.id, newValue)
    }

    // Also perform the API call directly
    try {
      const { tokenManager } = await import('../../lib/api')
      const API_BASE_URL = import.meta.env.VITE_API_URL || ''
      const token = tokenManager.getAccessToken()

      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/shortlist/`, {
        method: newValue ? 'POST' : 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendor_id: vendor.id,
          product_id: product.id,
          solution_family_id: solutionFamilyId || undefined,
        }),
      })
      // fetch() does not reject on 4xx/5xx — check explicitly so a failed
      // shortlist reverts the optimistic state instead of showing it as saved.
      if (!res.ok) {
        throw new Error(`Shortlist request failed: ${res.status}`)
      }
    } catch (err) {
      console.error('Failed to toggle shortlist:', err)
      // Revert both local and parent optimistic state on error.
      setIsShortlisted(!newValue)
      if (onShortlistToggle) {
        onShortlistToggle(product.id, vendor.id, !newValue)
      }
    } finally {
      setIsTogglingShortlist(false)
      onShortlistPersisted?.()
    }
  }

  return (
    <div
      onClick={onClick}
      data-testid="product-result-card"
      className="bg-card border border-border rounded-md overflow-hidden hover:border-primary/40
                 hover:shadow-md transition-all duration-200 cursor-pointer"
    >
      {/* Product Image */}
      <div className="relative h-40 sm:h-44 lg:h-52 bg-muted">
        {productImageUrl ? (
          <>
            <img
              src={productImageUrl}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
            {/* Image count indicator */}
            {imageCount > 1 && (
              <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded-full">
                +{imageCount - 1} more
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="w-16 h-16 text-muted-foreground/30" />
          </div>
        )}
        
        {/* Shortlist Star - top left */}
        {programId && (
          <button
            onClick={handleShortlistClick}
            disabled={isTogglingShortlist}
            className="absolute top-3 left-3 p-1.5 rounded-full bg-black/50 hover:bg-black/70 transition-all disabled:opacity-50 z-10"
            title={isShortlisted ? "Remove from shortlist" : "Add to shortlist"}
          >
            <Star
              className={`w-5 h-5 ${
                isShortlisted
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-white'
              }`}
            />
          </button>
        )}

        {/* Semantic Match Badge - below star if both present */}
        {semantic_score > 0.7 && (
          <div className={`absolute left-3 ${programId ? 'top-[4.5rem]' : 'top-3'}`}>
            <Badge variant="default" className="bg-primary/90">
              {Math.round(semantic_score * 100)}% match
            </Badge>
          </div>
        )}

        {/* Overall Score Badge */}
        <div data-testid="score-badge" className={`absolute top-3 right-3 px-3 py-1.5 rounded-full
                        ${overall.isScored ? 'font-bold text-lg' : 'text-xs font-medium'}
                        ${overallBadgeClasses(overall)}`}>
          {overall.text}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 lg:p-5">
        {/* Product Name & Vendor */}
        <div className="mb-2 sm:mb-3 lg:mb-4">
          <h3 className="font-semibold text-foreground text-base sm:text-lg lg:text-xl leading-tight line-clamp-1">
            {product.name}
          </h3>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{vendor.name}</span>
            {vendor.is_8a_certified && (
              <Badge variant="info" className="text-[10px] px-1.5 py-0">8(a)</Badge>
            )}
            {vendor.is_small_business && (
              <Badge variant="info" className="text-[10px] px-1.5 py-0">SB</Badge>
            )}
          </div>

          {/* Agency Badges */}
          <AgencyBadges vendor={vendor} />
        </div>

        {/* Description */}
        <p className="text-xs sm:text-sm text-muted-foreground line-clamp-4 lg:line-clamp-5 mb-4 leading-relaxed">
          {product.description || 'No description available'}
        </p>

        {/* Score Section - 2-bar for standalone, radar for program */}
        {isStandaloneMode ? (
          <TwoScoreBars scores={scores} />
        ) : (
          <div className="flex justify-center">
            <div className="scale-100 lg:scale-110 origin-center">
              <ScoreRadarChart scores={scores} size={140} />
            </div>
          </div>
        )}

        {/* Availability & Product URL */}
        <div className="flex flex-wrap items-center justify-between gap-1 mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-border">
          <span className="text-xs text-muted-foreground">
            {product.availability && product.availability !== 'unknown'
              ? product.availability.replace(/_/g, ' ')
              : 'Availability unknown'}
          </span>
          {product.product_url && (
            <a
              href={product.product_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
            >
              Product page
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductResultCard
